import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import {
  ArrowLeft,
  Watch,
  Footprints,
  Route,
  Flame,
  Heart,
  Scale,
  Loader2,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '@/hooks/useAuth';
import { usePremium } from '@/hooks/usePremium';
import {
  isHealthAvailable,
  requestHealthAuthorization,
  checkHealthAuthorization,
  readLatestFromNative,
  readHealthSamples,
  persistHealthConnectedState,
  getHealthConnectedState,
  syncHealthToSupabase,
  syncSamplesToSupabase,
  startYearHealthBackfillIfNeeded,
  HEALTH_DATA_TYPES,
} from '@/lib/appleHealth';
import type { HealthDataType } from '@capgo/capacitor-health';
import { format, subDays } from 'date-fns';
import { toast } from 'sonner';

const DATA_TYPE_CONFIG: Record<
  HealthDataType,
  { icon: React.ElementType; unit: string; color: string }
> = {
  steps: { icon: Footprints, unit: 'steps', color: '#00FFA3' },
  distance: { icon: Route, unit: 'km', color: '#0EA5E9' },
  calories: { icon: Flame, unit: 'kcal', color: '#F59E0B' },
  heartRate: { icon: Heart, unit: 'bpm', color: '#EF4444' },
  weight: { icon: Scale, unit: 'kg', color: '#8B5CF6' },
};

export const AppleHealth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isPremium } = usePremium();
  const { t } = useTranslation();
  const dataTypeLabels = useMemo<Record<HealthDataType, string>>(
    () => ({
      steps: t('healthData.metrics.steps.label'),
      distance: t('healthData.metrics.distance.label'),
      calories: t('common.calories'),
      heartRate: t('appleHealth.dataTypes.heartRate.label', { defaultValue: 'Heart Rate' }),
      weight: t('appleHealth.dataTypes.weight.label', { defaultValue: 'Weight' }),
    }),
    [t]
  );
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [syncingSamples, setSyncingSamples] = useState(false);
  const [authStatus, setAuthStatus] = useState<{
    readAuthorized: HealthDataType[];
    readDenied: HealthDataType[];
  } | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [chartData, setChartData] = useState<Record<HealthDataType, { date: string; value: number }[]>>({
    steps: [],
    distance: [],
    calories: [],
    heartRate: [],
    weight: [],
  });
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [latestValues, setLatestValues] = useState<Record<HealthDataType, number | null>>({
    steps: null,
    distance: null,
    calories: null,
    heartRate: null,
    weight: null,
  });

  const isConnected = authStatus && authStatus.readAuthorized.length > 0;

  const [unavailableReason, setUnavailableReason] = useState<string | null>(null);

  const verifyPlugin = async () => {
    try {
      const HealthModule = (await import('@capgo/capacitor-health')).Health;
      const v = await HealthModule.getPluginVersion();
      console.log('[AppleHealth] Plugin version:', v?.version);
      return true;
    } catch (e) {
      console.error('[AppleHealth] Plugin not reachable:', e);
      return false;
    }
  };

  const loadStatus = async () => {
    if (!Capacitor.isNativePlatform()) {
      setIsAvailable(false);
      setUnavailableReason('Web browser');
      return;
    }
    try {
      const result = await isHealthAvailable();
      setIsAvailable(result.available);
      setUnavailableReason(result.reason ?? null);

      // 1. Check persisted "connected" (native UserDefaults on iOS) - per-user
      const userId = user?.id;
      let connected = userId ? await getHealthConnectedState(userId) : false;
      if (connected) {
        setAuthStatus({ readAuthorized: [...HEALTH_DATA_TYPES], readDenied: [] });
        loadLatestNumbers();
        return;
      }

      // 2. Fallback: try reading - if we get any data, we have permission
      if (Capacitor.getPlatform() === 'ios' && userId) {
        try {
          const values = await readLatestFromNative();
          const hasData =
            (values.steps != null && values.steps > 0) ||
            (values.distance != null && values.distance > 0) ||
            (values.calories != null && values.calories > 0) ||
            values.heartRate != null ||
            values.weight != null;
          if (hasData) {
            await persistHealthConnectedState(userId);
            setAuthStatus({ readAuthorized: [...HEALTH_DATA_TYPES], readDenied: [] });
            loadLatestNumbers();
            return;
          }
        } catch {
          // read failed, continue to check auth
        }
      }

      const status = await checkHealthAuthorization(
        Capacitor.getPlatform() === 'ios' || !result.available
      );
      if (status && status.readAuthorized.length > 0 && userId) {
        setAuthStatus(status);
        await persistHealthConnectedState(userId);
        loadLatestNumbers();
      }
    } catch (err) {
      setIsAvailable(false);
      setUnavailableReason(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  useEffect(() => {
    verifyPlugin().then((ok) => {
      if (!ok) toast.error(t('appleHealth.toast.pluginNotLoaded'));
    });
    loadStatus();
  }, [location.pathname, user?.id]);

  const connectTimedOutRef = useRef(false);
  const connectInProgressRef = useRef(false);

  const handleConnect = async (skipAvailabilityCheck = false) => {
    if (!Capacitor.isNativePlatform()) {
      toast.error(t('appleHealth.toast.iosOnly'));
      return;
    }
    if (connectInProgressRef.current) {
      toast.info(t('appleHealth.toast.connectionInProgress'));
      return;
    }
    connectInProgressRef.current = true;
    setIsConnecting(true);
    connectTimedOutRef.current = false;
    const timeoutMs = 12_000; // Sheet should appear within seconds
    const timeoutId = setTimeout(() => {
      connectTimedOutRef.current = true;
      connectInProgressRef.current = false;
      setIsConnecting(false);
      toast.error(t('appleHealth.toast.connectTimeout'));
    }, timeoutMs);
    try {
      console.log('[AppleHealth] Calling requestHealthAuthorization, skipCheck:', skipAvailabilityCheck);
      const status = await requestHealthAuthorization(skipAvailabilityCheck, true, {
        extendedWrite: isPremium,
      });
      console.log('[AppleHealth] requestHealthAuthorization resolved:', status);
      clearTimeout(timeoutId);
      if (connectTimedOutRef.current) return;
      setAuthStatus(status as any);
      if (status.readAuthorized.length > 0 && user?.id) {
        await persistHealthConnectedState(user.id);
        toast.success(t('appleHealth.toast.connectedSyncing'));
        loadLatestNumbers();
        // Sync 30-day backfill immediately on first connection (native connect flow dismisses without loading data)
        try {
          await syncHealthToSupabase(user.id, { forceSync: true, lookbackDays: 30, skipSamples: true });
          startYearHealthBackfillIfNeeded(user.id);
        } catch (e) {
          console.warn('[AppleHealth] Initial sync failed, will retry on next foreground:', e);
        }
      } else {
        toast.error(t('appleHealth.toast.accessDenied'));
      }
    } catch (error) {
      clearTimeout(timeoutId);
      connectInProgressRef.current = false;
      if (connectTimedOutRef.current) return;
      console.error('[AppleHealth] Health auth error:', error);
      const msg = error instanceof Error ? error.message : '';
      const isCanceled = /canceled|cancelled/i.test(msg);
      toast.error(
        isCanceled
          ? t('appleHealth.toast.permissionCanceled')
          : t('appleHealth.toast.connectFailed')
      );
    } finally {
      if (!connectTimedOutRef.current) {
        connectInProgressRef.current = false;
        setIsConnecting(false);
      }
    }
  };

  const [loadingLatest, setLoadingLatest] = useState(false);

  const loadLatestNumbers = async () => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;
    setLoadingLatest(true);
    try {
      const values = await readLatestFromNative();
      setLatestValues(values);
    } catch (e) {
      console.warn('[AppleHealth] readLatestFromNative failed:', e);
    } finally {
      setLoadingLatest(false);
    }
  };

  const loadChartData = async (types: HealthDataType[]) => {
    if (types.length === 0) return;
    setLoadingCharts(true);
    const endDate = new Date();
    const startDate = subDays(endDate, 7);
    const opts = { startDate, endDate, limit: 500 };

    try {
      // Run all reads in parallel for speed
      const results = await Promise.allSettled(
        types.map((dataType) => readHealthSamples(dataType, opts))
      );

      const newChartData = { ...chartData };
      const newLatest = { ...latestValues };

      results.forEach((result, i) => {
        const dataType = types[i];
        const samples = result.status === 'fulfilled' ? result.value : [];

        // Aggregate by day
        const byDay: Record<string, number> = {};
        samples.forEach((s) => {
          const day = format(new Date(s.startDate), 'yyyy-MM-dd');
          if (dataType === 'heartRate') {
            byDay[day] = byDay[day] ? Math.round((byDay[day] + s.value) / 2) : Math.round(s.value);
          } else {
            byDay[day] = (byDay[day] || 0) + s.value;
          }
        });

        const sorted = Object.entries(byDay)
          .map(([dateStr, value]) => ({
            date: format(new Date(dateStr + 'T12:00:00'), 'MMM d'),
            dateSort: dateStr,
            value,
          }))
          .sort((a, b) => a.dateSort.localeCompare(b.dateSort))
          .map(({ date, value }) => ({ date, value }));

        newChartData[dataType] = sorted;

        if (samples.length > 0) {
          const latest = samples[0];
          newLatest[dataType] = dataType === 'distance' ? latest.value / 1000 : latest.value;
        }
      });

      setChartData(newChartData);
      setLatestValues(newLatest);
    } catch (error) {
      console.error('[AppleHealth] Error loading health data:', error);
      toast.error(t('appleHealth.toast.loadFailed'));
    } finally {
      setLoadingCharts(false);
    }
  };

  useEffect(() => {
    if (isConnected && authStatus?.readAuthorized && Capacitor.getPlatform() === 'ios') {
      loadLatestNumbers();
    }
  }, [isConnected]);

  if (!Capacitor.isNativePlatform()) {
    return (
      <div className="bg-gradient-bg p-4 pb-24">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-normal">{t('appleHealth.title')}</h1>
        </div>
        <Card className="bg-gradient-card border-0">
          <CardContent className="pt-6">
            <p className="text-muted-foreground text-center">
              {t('appleHealth.iosOnlyMessage')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isAvailable === false) {
    const isSimulator = unavailableReason?.toLowerCase().includes('not available') ?? false;
    return (
      <div className="bg-gradient-bg p-4 pb-24">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-normal">{t('appleHealth.title')}</h1>
        </div>
        <Card className="bg-gradient-card border-0">
          <CardContent className="pt-6 space-y-4">
            <p className="text-muted-foreground text-center">
              {t('appleHealth.unavailable.body')}
            </p>
            <p className="text-xs text-muted-foreground text-center">
              {isSimulator
                ? t('appleHealth.unavailable.simulator')
                : t('appleHealth.unavailable.retryHint')}
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setUnavailableReason(null);
                loadStatus();
              }}
            >
              {t('appleHealth.unavailable.tryAgain')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-gradient-bg p-4 pb-24 min-h-full">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-normal">{t('appleHealth.title')}</h1>
      </div>

      {/* Connections - Top of page */}
      <div className="mb-6">
        <h2 className="text-sm font-medium text-muted-foreground mb-3">{t('appleHealth.connectionsTitle')}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {HEALTH_DATA_TYPES.map((type) => {
            const config = DATA_TYPE_CONFIG[type];
            const Icon = config.icon;
            const authorized = authStatus?.readAuthorized?.includes(type) ?? false;
            const value = latestValues[type];

            return (
              <Card
                key={type}
                className={`bg-gradient-card border-0 p-3 ${
                  authorized ? 'ring-1 ring-primary/30' : 'opacity-60'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className="h-4 w-4 text-primary shrink-0" style={{ color: config.color }} />
                    <span className="text-sm font-medium truncate">{dataTypeLabels[type]}</span>
                  </div>
                  {authorized ? (
                    <CheckCircle className="h-4 w-4 text-primary shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 text-muted-foreground shrink-0" />
                  )}
                </div>
                {authorized && value != null && (
                  <p className="text-lg font-bold mt-1 truncate" style={{ color: config.color }}>
                    {type === 'distance' ? value.toFixed(2) : Math.round(value)}
                    <span className="text-xs font-normal text-muted-foreground ml-1">{config.unit}</span>
                  </p>
                )}
              </Card>
            );
          })}
        </div>

        {!isConnected && (
          <div className="mt-4 space-y-2">
            <Button
              onClick={() => handleConnect(false)}
              disabled={isConnecting}
              className="w-full bg-gradient-primary text-black"
            >
              {isConnecting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('appleHealth.connect.connecting')}
                </>
              ) : (
                <>
                  <Watch className="mr-2 h-4 w-4" />
                  {t('appleHealth.connect.cta')}
                </>
              )}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              {t('appleHealth.connect.instructions')}
            </p>
            {isConnecting && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-muted-foreground"
                  onClick={() => {
                    connectTimedOutRef.current = true;
                    setIsConnecting(false);
                  }}
                >
                  {t('appleHealth.connect.cancel')}
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  {t('appleHealth.connect.sheetHint')}
                </p>
              </>
            )}
          </div>
        )}

        {isConnected && user && (
          <div className="mt-4">
            <Button
              variant="outline"
              className="w-full"
              disabled={syncingSamples}
              onClick={async () => {
                setSyncingSamples(true);
                try {
                  const res = await syncSamplesToSupabase(user.id);
                  if (res.synced) {
                    toast.success(
                      res.count != null && res.count > 0
                        ? t('appleHealth.toast.syncedSamples', { count: res.count })
                        : t('appleHealth.toast.sampleSyncCompleted')
                    );
                  } else {
                    toast.error(res.error ?? t('appleHealth.toast.sampleSyncFailed'));
                  }
                } catch (e) {
                  toast.error(e instanceof Error ? e.message : t('appleHealth.toast.sampleSyncFailed'));
                } finally {
                  setSyncingSamples(false);
                }
              }}
            >
              {syncingSamples ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('appleHealth.sync.syncingSamples')}
                </>
              ) : (
                t('appleHealth.sync.cta')
              )}
            </Button>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              {t('appleHealth.sync.helpText')}
            </p>
          </div>
        )}
      </div>

      {/* Graphs - Scroll down */}
      {isConnected && (
        <div className="space-y-6">
          <h2 className="text-sm font-medium text-muted-foreground">{t('appleHealth.graphs.last7Days')}</h2>

          {loadingCharts ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            authStatus?.readAuthorized?.map((type) => {
              const config = DATA_TYPE_CONFIG[type];
              const data = chartData[type];
              const Icon = config.icon;

              return (
                <Card key={type} className="bg-gradient-card border-0 overflow-hidden">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Icon className="h-4 w-4" style={{ color: config.color }} />
                      {dataTypeLabels[type]}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {(!data || data.length === 0) ? (
                      <p className="text-sm text-muted-foreground py-8 text-center">
                        {t('appleHealth.graphs.empty')}
                      </p>
                    ) : (
                    <div className="h-[180px]">
                      <ResponsiveContainer width="100%" height="100%">
                        {type === 'heartRate' || type === 'weight' ? (
                          <LineChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                            <XAxis
                              dataKey="date"
                              stroke="rgba(255,255,255,0.5)"
                              fontSize={10}
                              tickLine={false}
                            />
                            <YAxis
                              stroke="rgba(255,255,255,0.5)"
                              fontSize={10}
                              tickLine={false}
                              tickFormatter={(v) => (type === 'weight' ? v.toFixed(1) : String(v))}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: 'hsl(220 10% 14%)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '8px',
                              }}
                              formatter={(value: number) => [
                                type === 'weight' ? `${value} kg` : `${value} ${config.unit}`,
                                dataTypeLabels[type],
                              ]}
                            />
                            <Line
                              type="monotone"
                              dataKey="value"
                              stroke={config.color}
                              strokeWidth={2}
                              dot={{ fill: config.color, r: 3 }}
                              activeDot={{ r: 5 }}
                            />
                          </LineChart>
                        ) : (
                          <BarChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                            <XAxis
                              dataKey="date"
                              stroke="rgba(255,255,255,0.5)"
                              fontSize={10}
                              tickLine={false}
                            />
                            <YAxis
                              stroke="rgba(255,255,255,0.5)"
                              fontSize={10}
                              tickLine={false}
                              tickFormatter={(v) =>
                                type === 'distance' ? `${(v / 1000).toFixed(1)}` : String(v)
                              }
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: 'hsl(220 10% 14%)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '8px',
                              }}
                              formatter={(value: number) => [
                                type === 'distance'
                                  ? `${(value / 1000).toFixed(2)} km`
                                  : `${value} ${config.unit}`,
                                dataTypeLabels[type],
                              ]}
                            />
                            <Bar
                              dataKey="value"
                              fill={config.color}
                              radius={[4, 4, 0, 0]}
                            />
                          </BarChart>
                        )}
                      </ResponsiveContainer>
                    </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
